import { appColors } from '@base/useTheme';
import { EntityKey, FolderItemType, ModelObjectType } from '@common/api';
import { CheckboxField } from '@components/form';
import { FlexCol, FlexRow } from '@components/layout/Flex';
import { Modal } from '@components/modal/Modal';
import { parseShortKey } from '@data/entity/util';
import { modelApi } from '@data/rest/api';
import { SideNavWidget } from '@features/sidenav/SideNav.style';
import { CheckboxFoldersTree } from '@features/sidenav/tree-checkbox/CheckboxFoldersTree';
import { useI18nContext } from '@i18n/i18n-react';
import { useAuth } from '@providers/AuthProvider';
import { tail } from '@util/array';
import { downloadFile } from '@util/helpers';
import { useState } from 'react';
import { useEntityContext } from '@providers/EntityProvider';
import toast from 'react-hot-toast';

const ALLOWED_TYPES = [FolderItemType.DimensionTable, FolderItemType.Variable];

export const ModelExportModal = () => {
    const { LL } = useI18nContext();
    const { tenant } = useAuth();
    const { getEntity } = useEntityContext();

    const [loading, setLoading] = useState<boolean>(false);
    const [message, setMessage] = useState('');
    const [selectedEntityPaths, setSelectedEntityPaths] = useState<string[][]>([]);
    const [exportCells, setExportCells] = useState<ModelObjectType.CELL | null>(null);
    const [exportMembers, setExportMembers] = useState<ModelObjectType.DIMENSION_MEMBER | null>(null);

    const handleExport = async () => {
        const types = [ModelObjectType.VARIABLE, ModelObjectType.DIMENSION_TABLE];
        if (exportCells) types.push(exportCells);
        if (exportMembers) types.push(exportMembers);

        setLoading(true);
        setMessage(LL.EXPORTING_MODEL_ITEMS());
        const id = toast.loading(LL.EXPORTING());

        const selectedFolderPaths = selectedEntityPaths
            .map((path) => {
                const ek = parseShortKey(tail(path)) as EntityKey;
                const e = getEntity(ek);

                if (e?.folder) return [...e.folder.path, e.folder.id ?? e.id];
                return null;
            })
            .filter((p) => !!p) as number[][];

        try {
            const model = await modelApi.exportModel({
                itemPaths: selectedFolderPaths,
                types,
            });
            const filename = `${tenant?.toLowerCase().replace(/\s+/g, '_') ?? 'unknown'}_model_export.fsm`;
            const modelText = JSON.stringify(model);
            let formattedModelText;
            try {
                formattedModelText = JSON.stringify(JSON.parse(modelText), null, 4);
            } catch (e) {
                formattedModelText = modelText;
                console.error('Formatting failed:', e);
            }
            const file = new File([modelText], filename, { type: 'application/json' });
            setMessage((current) => current + `\nModel exported: \n${formattedModelText}`);
            downloadFile(file, filename);
            toast.dismiss(id);
            toast.success(LL.SUCCESSFULLY_LOADED_DATA());
        } catch (error) {
            toast.dismiss(id);
            toast.error(LL.FAILED_TO_LOAD_MODEL());
            setMessage((current) => current + `\nError, failed to export model: \n${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal.Content width={804} height={614}>
            <FlexRow style={{ width: '100%', height: '100%' }}>
                <SideNavWidget style={{ flex: 3, marginLeft: -20, marginRight: 20 }}>
                    <span style={{ color: appColors.ui.white, marginBottom: 16 }}>{LL.CHOOSE_ITEMS_TO_EXPORT()}:</span>
                    <CheckboxFoldersTree
                        allowedTypes={ALLOWED_TYPES}
                        value={selectedEntityPaths}
                        onChange={setSelectedEntityPaths}
                    />
                </SideNavWidget>
                <FlexCol style={{ flex: 5 }}>
                    <Modal.Header title={LL.MODEL_EXPORT()} />
                    <Modal.Body
                        style={{
                            padding: 10,
                            paddingBottom: 0,
                            textOverflow: 'clip',
                            overflow: 'auto',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            marginTop: 20,
                            marginBottom: 20,
                            borderRadius: 10,
                            borderWidth: 1,
                            borderColor: appColors.neutral.w200,
                            borderStyle: 'solid',
                        }}
                    >
                        <div style={{ marginBottom: 10 }}>{message}</div>
                    </Modal.Body>
                    <FlexRow $justify="space-between">
                        <FlexRow $align="center" $gap={16} style={{ paddingBottom: 20 }}>
                            <CheckboxField
                                size="small"
                                label={LL.INCLUDE_CELLS()}
                                value={!!exportCells}
                                onChange={() => setExportCells(exportCells ? null : ModelObjectType.CELL)}
                            />
                            <CheckboxField
                                size="small"
                                label={LL.INCLUDE_MEMBERS()}
                                value={!!exportMembers}
                                onChange={() =>
                                    setExportMembers(exportMembers ? null : ModelObjectType.DIMENSION_MEMBER)
                                }
                            />
                        </FlexRow>
                        <Modal.Footer
                            doneType="primary"
                            doneLabel={LL.EXPORT()}
                            cancelLabel={LL.CLOSE()}
                            done={handleExport}
                            loading={loading}
                        />
                    </FlexRow>
                </FlexCol>
            </FlexRow>
        </Modal.Content>
    );
};
